import { StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';
import { extractResumeDataDocumentSection } from "../../../hooks/useResumeDocumentSection";
import { extractResumeProperties } from "../../../hooks/useResumeProperties";
import { getNativeLanguageCodes } from "../../../utils/getNativeLanguageCodes";

export default function EuropassPdfLanguageSkillsSection( { resumeData, translations } ) {

    const { languageSkills } = extractResumeDataDocumentSection( resumeData );
    const { documentLanguage } = extractResumeProperties( resumeData );

    const nativeLanguageCodes = getNativeLanguageCodes( languageSkills );

    const styles = StyleSheet.create( {
        section: {
            flexDirection: 'row',
            marginBottom: 2,
            fontSize: 10,
        },
        sectionTable: {
            flexDirection: 'row',
            fontSize: 10,
        },
        table: {
            width: '100%',
        },
        tableHeader: {
            backgroundColor: "#ffffff",
            color: "#000000",
            fontWeight: 'bold',
        },
        tableRow: {
            width: '50%',
            textAlign: "center",
            marginTop: 2,
            marginBottom: 2,
        },
        row1: {
            width: '30%',
        },
        row2: {
            width: '25%',
        },
        row3: {
            width: '25%',
        },
        bold: {
            fontWeight: 'bold',
        },
        sectionTitle: {
            fontSize: 12,
            fontWeight: 'bold',
            marginBottom: 2,
            textTransform: 'uppercase',
            color: '#0B86C8',
        },
        titleContainer: {
            flexDirection: 'row',
            alignItems: 'center',
        },
        line: {
            flexGrow: 1,
            height: 1,
            backgroundColor: '#0B86C8',
            marginLeft: 5,
        },
        divider: {
            borderBottomColor: '#0B86C8',
            borderBottomWidth: 1,
            marginTop: 2,
            marginBottom: 2,
        },
        lineTitle: {
            height: 1,
            backgroundColor: "#979797",
            marginRight: 10,
            marginBottom: 5,
        }
    } );

    return (
        <View style={ { marginTop: 0, paddingHorizontal: 20, marginRight: 40 } } wrap={ false }>
            <View style={ { flexDirection: "row", marginBottom: 4 } }>
                <Text style={ { fontSize: 11, marginRight: 3 } }>•</Text>
                <Text style={ { fontWeight: "bold", textTransform: "uppercase", fontSize: 9, marginTop: 2 } }>
                    { translations.pdf.languageSkillsTitle }
                </Text>
            </View>
            <View style={ [ styles.lineTitle, { marginTop: 1 } ] }/>
            <Text style={ { fontSize: 8, color: "#000000" } }>
                { translations.pdf.nativeLanguagesLabel }{ ' ' }
                <Text style={ { fontWeight: "bold", fontSize: 8, textTransform: "uppercase" } }>
                    { languageSkills
                        .filter( skill => skill.language )
                        .filter( skill => nativeLanguageCodes.includes( skill.language.code ) )
                        .sort( ( a, b ) => a.order - b.order )
                        .map( ( skill ) => ( skill.language?.translations?.[ documentLanguage ] ?? skill.language?.code ?? '' ) )
                        .join( ', ' ) }
                </Text>
            </Text>
            <Text style={ { fontSize: 8, color: "#000000", marginTop: 4 } }>
                { translations.pdf.otherLanguagesLabel }
            </Text>
            <View style={ styles.table }>
                <View style={ [ styles.section, styles.tableHeader ] }>
                    <Text style={ {
                        width: '15%',
                        textAlign: "center",
                        marginTop: 2,
                        fontSize: 8,
                        textTransform: "uppercase"
                    } }>
                        { ' ' }
                    </Text>
                    <Text style={ {
                        width: '30%',
                        textAlign: "center",
                        marginTop: 2,
                        fontSize: 8,
                        textTransform: "uppercase"
                    } }>
                        { translations.pdf.comprehensionHeader }
                    </Text>
                    <Text style={ {
                        width: '30%',
                        textAlign: "center",
                        marginTop: 2,
                        fontSize: 8,
                        textTransform: "uppercase"
                    } }>
                        { translations.pdf.oralExpressionHeader }
                    </Text>
                    <Text style={ {
                        width: '30%',
                        textAlign: "center",
                        marginTop: 2,
                        fontSize: 8,
                        textTransform: "uppercase"
                    } }>
                        { translations.pdf.writingExpressionHeader }
                    </Text>
                </View>
                <View style={ styles.lineTitle }/>
                <View style={ [
                    styles.sectionTable,
                    { alignItems: "center", marginBottom: 10 }
                ] }>
                    <Text style={ { width: '15%', textAlign: "center" } }>
                        { ' ' }
                    </Text>
                    <Text style={ { textAlign: "center", fontSize: 8, width: '15%', flexWrap: "wrap" } }>
                        { translations.pdf.listeningComprehensionHeader }
                    </Text>
                    <Text style={ { textAlign: "center", fontSize: 8, width: '15%', flexWrap: "wrap" } }>
                        { translations.pdf.readingComprehensionHeader }
                    </Text>
                    <Text style={ { textAlign: "center", fontSize: 8, width: '15%', flexWrap: "wrap" } }>
                        { translations.pdf.continuousOralExpressionHeader }
                    </Text>
                    <Text style={ { textAlign: "center", fontSize: 8, width: '15%', flexWrap: "wrap" } }>
                        { translations.pdf.oralInteractionHeader }
                    </Text>
                    <Text style={ { textAlign: "center", fontSize: 8, width: '15%' } }>
                        { ' ' }
                    </Text>
                </View>
                <View style={ styles.lineTitle }/>
                { languageSkills
                    .filter( skill => skill.language )
                    .filter( skill => !nativeLanguageCodes.includes( skill.language.code ) )
                    .sort( ( a, b ) => a.order - b.order )
                    .map( ( skill, index ) => (
                        <React.Fragment key={ index }>
                            { index > 0 && ( <View style={ styles.lineTitle }/> ) }
                            <View style={ styles.section }>
                                <Text style={ [ styles.row1, styles.bold ] }>
                                    { skill.language?.translations?.[ documentLanguage ] ?? skill.language?.code ?? '' }
                                </Text>
                                <Text style={ [ styles.row2 ] }>
                                    { skill.listeningComprehensionLevel?.translations?.[ documentLanguage ] ?? '' }
                                </Text>
                                <Text style={ [ styles.row2 ] }>
                                    { skill.readingComprehensionLevel?.translations?.[ documentLanguage ] ?? '' }
                                </Text>
                                <Text style={ [ styles.row3 ] }>
                                    { skill.continuousOralExpressionLevel?.translations?.[ documentLanguage ] ?? '' }
                                </Text>
                                <Text style={ [ styles.row3 ] }>
                                    { skill.oralInteractionLevel?.translations?.[ documentLanguage ] ?? '' }
                                </Text>
                                <Text style={ [ styles.row3 ] }>
                                    { skill.writingExpressionLevel?.translations?.[ documentLanguage ] ?? '' }
                                </Text>
                            </View>
                        </React.Fragment>
                    ) ) }
                <Text style={ { fontSize: 6, color: "#000000" } }>
                    Niveaux :{ ' ' }
                    A1 et A2 : utilisateur de base;{ ' ' }
                    B1 et B2 : utilisateur indépendant;{ ' ' }
                    C1 et C2 : utilisateur expérimenté.
                </Text>
            </View>
        </View>
    );
}
